import React, { useState, useEffect } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getProfileImageWithFallback } from '../../utils/profileImageUtils';
import { getTypeConfig } from '../../utils/typeUtils';
import { useAuth } from '../../hooks/useAuth';
import { useTCoins } from '../../context/TCoinsContext';
import GiftButton from '../Gift/GiftButton';
import TCoinsDisplay from '../TCoins/TCoinsDisplay';
import DailyBonus from '../TCoins/DailyBonus';
import CheckinBanner from '../Checkin/CheckinBanner';
// import mapImage from '../../assets/map22.png';

const MapHome = () => {
  const { currentUser: user } = useAuth();
  const { loadTCoins } = useTCoins();
  
  // เพิ่ม CSS animation สำหรับ float effect
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      .animate-float {
        animation: float 3s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // โหลด T Coins เมื่อ user login
  useEffect(() => {
    if (user) {
      loadTCoins(user.uid);
    }
  }, [user, loadTCoins]);
  
  // เปิดใช้งาน Firestore อีกครั้ง (ลบ orderBy ออกชั่วคราว)
  const [girls, loading, error] = useCollection(
    query(
      collection(db, 'girls')
    )
  );
  

  // ใช้ localStorage เพื่อเก็บข้อมูลการจอง (ดึงครั้งเดียวตอน login)
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState(false);

  // สถิติแบบ real-time
  const [stats, setStats] = useState({
    totalGirls: 0,
    totalBookings: 0,
    todayBookings: 0,
    confirmedBookings: 0
  });

  // State สำหรับ fade effect
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // State สำหรับเก็บข้อมูล plan จาก users collection
  const [girlPlans, setGirlPlans] = useState({});





  // ฟังก์ชันสำหรับดึงข้อมูล plan จาก users collection
  const fetchGirlPlans = async (girlsData) => {
    try {
      const subscriptions = {};
      
      for (const girl of girlsData) {
        if (girl.userId) {
          try {
            const userRef = doc(db, 'users', girl.userId);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              const userData = userSnap.data();
              subscriptions[girl.id] = userData.tier || userData.plan || 'free';
              console.log(`👧 ${girl.nickname}: tier = "${userData.tier}", plan = "${userData.plan}" → final = "${subscriptions[girl.id]}" (from users collection)`);
            } else {
              subscriptions[girl.id] = 'free';
            }
          } catch (error) {
            console.error(`Error fetching plan for girl ${girl.id}:`, error);
            subscriptions[girl.id] = 'free';
          }
        } else {
          subscriptions[girl.id] = 'free';
        }
      }
      
      setGirlPlans(subscriptions);
      console.log('✅ ดึงข้อมูล plan จาก users collection สำเร็จ:', subscriptions);
    } catch (error) {
      console.error('❌ ไม่สามารถดึงข้อมูล subscription:', error);
    }
  };

  // ข้อมูลสถิติสำหรับการแสดงผลแบบวน
  const statItems = [
    { 
      id: 'girls', 
      icon: '👧', 
      value: stats.totalGirls, 
      label: 'น้องว่าง', 
      color: 'from-pink-500 to-pink-600',
      position: 'bottom-32 left-8',
      rotation: '-rotate-12'
    },
    { 
      id: 'total', 
      icon: '📊', 
      value: stats.totalBookings, 
      label: 'จองทั้งหมด', 
      color: 'from-blue-500 to-blue-600',
      position: 'bottom-16 left-12',
      rotation: 'rotate-6'
    },
    { 
      id: 'confirmed', 
      icon: '✅', 
      value: stats.confirmedBookings, 
      label: 'ยืนยันแล้ว', 
      color: 'from-purple-500 to-purple-600',
      position: 'bottom-8 right-8',
      rotation: '-rotate-12'
    },
    { 
      id: 'today', 
      icon: '📅', 
      value: stats.todayBookings, 
      label: 'จองวันนี้', 
      color: 'from-green-500 to-green-600',
      position: 'bottom-24 right-12',
      rotation: 'rotate-12'
    }
  ];

  // ฟังก์ชันดึงข้อมูลการจองจาก localStorage หรือ Firestore
  const fetchBookings = async (isManual = false) => {
    try {
      setBookingsLoading(true);
      
      // ถ้าเป็น manual refresh หรือไม่มีข้อมูลใน localStorage
      if (isManual || !localStorage.getItem('bookingsData')) {
        const bookingsQuery = query(collection(db, 'bookings'));
        const querySnapshot = await getDocs(bookingsQuery);
        const bookingsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // บันทึกลง localStorage
        localStorage.setItem('bookingsData', JSON.stringify(bookingsData));
        localStorage.setItem('bookingsLastFetch', new Date().toISOString());
        
        setBookings(bookingsData);
        setLastFetchTime(new Date());
      } else {
        // โหลดจาก localStorage
        const storedData = localStorage.getItem('bookingsData');
        const storedTime = localStorage.getItem('bookingsLastFetch');
        
        if (storedData) {
          setBookings(JSON.parse(storedData));
          setLastFetchTime(new Date(storedTime));
        }
      }
      
      setHasInitialLoad(true);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  // โหลดข้อมูลจาก localStorage เมื่อเข้าหน้า
  useEffect(() => {
    if (!hasInitialLoad) {
      fetchBookings();
    }
  }, [hasInitialLoad]);

  // คำนวณสถิติ
  useEffect(() => {
    if (girls && bookings.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayBookings = bookings.filter(booking => {
        const bookingDate = booking.bookingDate;
        
        // ตรวจสอบว่าเป็น Firestore Timestamp หรือไม่
        if (bookingDate && typeof bookingDate.toDate === 'function') {
          return bookingDate.toDate() >= today;
        } else if (bookingDate instanceof Date) {
          return bookingDate >= today;
        } else if (typeof bookingDate === 'string') {
          const date = new Date(bookingDate);
          return !isNaN(date.getTime()) && date >= today;
        }
        
        return false;
      });

      const confirmedBookings = bookings.filter(booking => 
        booking.status === 'confirmed'
      );

      setStats({
        totalGirls: girls.docs.length,
        totalBookings: bookings.length,
        todayBookings: todayBookings.length,
        confirmedBookings: confirmedBookings.length
      });
    }
  }, [girls, bookings]);

  // ดึงข้อมูล plan เมื่อข้อมูล girls เปลี่ยน
  useEffect(() => {
    if (girls && girls.docs.length > 0) {
      const girlsData = girls.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      fetchGirlPlans(girlsData);
    }
  }, [girls]);



  // Effect สำหรับสลับการแสดงผลแบบวน
  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentStatIndex((prevIndex) => (prevIndex + 1) % statItems.length);
        setIsVisible(true);
      }, 500); // รอให้ fade out เสร็จก่อนเปลี่ยน
    }, 3000); // เปลี่ยนทุก 3 วินาที

    return () => clearInterval(interval);
  }, [statItems.length]);

  // Debug: ตรวจสอบ path ของไฟล์ (ปิดแล้ว)
  // console.log('Public URL:', process.env.PUBLIC_URL || '');
  // console.log('Map image path:', (process.env.PUBLIC_URL || '') + '/map22.png');

  // แสดงภาพแม้มี error
  const girlCount = girls?.docs.length || 0;

  // แสดง loading indicator เมื่อระบบกำลังโหลด
  if (loading && !hasInitialLoad) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-blue-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-blue-50 to-purple-100 relative overflow-hidden">
      {/* Check-in Banner */}
      <CheckinBanner />
      
      {/* Background decorative elements - ลดลงสำหรับมือถือ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating stars - ปรับขนาดและตำแหน่งสำหรับมือถือ */}
        <div className="absolute top-16 left-4 text-yellow-300 text-lg animate-pulse sm:text-2xl">✨</div>
        <div className="absolute top-24 right-8 text-pink-300 text-sm animate-pulse animation-delay-1000 sm:text-xl">⭐</div>
        <div className="absolute top-32 left-1/3 text-blue-300 text-sm animate-pulse animation-delay-2000 sm:text-lg">💫</div>
        <div className="absolute bottom-32 right-4 text-purple-300 text-sm animate-pulse animation-delay-1000 sm:text-xl">✨</div>
        <div className="absolute bottom-16 left-8 text-yellow-300 text-sm animate-pulse animation-delay-2000 sm:text-lg">⭐</div>
      </div>

      <div className="relative z-10 p-3 sm:p-6" style={{ paddingTop: '80px' }}>


        {/* Top Bar - T Coins & Create Trip Button */}
        {user && (
          <div className="flex justify-between items-center mb-4 sm:mb-8">
            {/* T Coins Display */}
            <div className="flex space-x-4">
              <TCoinsDisplay compact={true} />
              <DailyBonus />
            </div>
            
            {/* Create Trip Button */}
            <button
              onClick={() => window.location.href = '/trip-plans'}
              className="bg-blue-500 text-white px-4 py-2 text-sm rounded-lg hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 sm:px-6 sm:py-3 sm:text-base sm:rounded-xl"
            >
              สร้าง Trip ใหม่
            </button>
          </div>
        )}

        {/* แผนที่การ์ตูน - ปรับสำหรับมือถือ */}
        <div className="flex justify-center mb-4 sm:mb-8">
          <div className="relative w-full max-w-sm sm:max-w-lg">
            
            {/* แผนที่การ์ตูน - ภาพชัด */}
            <div className="w-full h-64 sm:h-80 rounded-lg shadow-lg border-2 border-blue-200 relative overflow-hidden">
              {/* Background Image - แสดงชัดเจน */}
              <img
                src="/map22.png"
                alt="แผนที่กาญจนบุรี"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ zIndex: 1 }}
              />
            </div>
            
            
          </div>
        </div>



        {/* รายการน้องๆ - ปรับสำหรับมือถือ */}
        <div className="mt-4 sm:mt-8">
          <h2 className="text-lg sm:text-xl font-bold text-center mb-4 sm:mb-6">รายการน้องในเมืองฮักกาญ</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            {girls?.docs.map((girl) => {
              const girlData = girl.data();
              // Debug: แสดงข้อมูลทั้งหมดที่ดึงมาจาก girls collection
              console.log(`👧 ${girlData.nickname}:`, {
                plan: girlData.plan,
                bio: girlData.bio,
                description: girlData.description,
                type: girlData.type,
                grades: girlData.grades,
                location: girlData.location,
                lineType: girlData.lineType,
                lineContact: girlData.lineContact,
                servicePrice: girlData.servicePrice,
                coinPrice: girlData.coinPrice
              });
              return (
                <div key={girl.id} className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                      backgroundImage: `url(${getProfileImageWithFallback(girlData, '/default-avatar.png')})`,
                    }}
                  >
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-2 sm:p-4 h-80 sm:h-96 flex flex-col justify-end">
                    {/* Content Container - ปรับให้เหมาะกับมือถือ */}
                    <div className="space-y-1 sm:space-y-2">
                      {/* Name */}
                      <h3 className="font-bold text-white text-sm sm:text-lg drop-shadow-lg">{girlData.nickname}</h3>
                      
                      {/* Rating */}
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={`text-xs sm:text-sm ${i < (girlData.grades || 4) ? 'text-yellow-400' : 'text-gray-400'}`}>⭐</span>
                        ))}
                      </div>
                      
                      {/* Description */}
                      {(girlData.description || girlData.bio) && (
                        <div className="text-white text-xs sm:text-sm leading-relaxed">
                          {(() => {
                            const desc = girlData.description || girlData.bio || '';
                            console.log(`👧 ${girlData.nickname}: คำอธิบาย = "${desc}" (from ${girlData.description ? 'description' : 'bio'})`);
                            return desc.length > 20 
                              ? `${desc.substring(0, 20)}...` 
                              : desc;
                          })()}
                        </div>
                      )}
                        
                      {/* Badges */}
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Plan Badge */}
                        {(() => {
                          const plan = girlPlans[girl.id] || girlData.plan || 'free';
                          console.log(`👧 ${girlData.nickname}: plan = "${plan}" (from ${girlPlans[girl.id] ? 'users' : 'girls'} collection)`);
                          
                          switch (plan) {
                            case 'free':
                              return (
                                <div className="bg-white/20 backdrop-blur-sm text-white px-1 py-0.5 rounded-full text-xs font-medium border border-white/30 sm:px-2 sm:py-1">
                                  🆓 Free
                                </div>
                              );
                            case 'basic':
                              return (
                                <div className="bg-blue-500/80 backdrop-blur-sm text-white px-1 py-0.5 rounded-full text-xs font-medium border border-blue-300/50 sm:px-2 sm:py-1">
                                  🥉 Basic
                                </div>
                              );
                            case 'premium':
                              return (
                                <div className="bg-purple-500/80 backdrop-blur-sm text-white px-1 py-0.5 rounded-full text-xs font-medium border border-purple-300/50 sm:px-2 sm:py-1">
                                  🥈 Premium
                                </div>
                              );
                            case 'vip':
                              return (
                                <div className="bg-yellow-500/80 backdrop-blur-sm text-white px-1 py-0.5 rounded-full text-xs font-medium border border-yellow-300/50 sm:px-2 sm:py-1">
                                  👑 VIP
                                </div>
                              );
                            default:
                              return (
                                <div className="bg-red-500/80 backdrop-blur-sm text-white px-1 py-0.5 rounded-full text-xs font-medium border border-red-300/50 sm:px-2 sm:py-1">
                                  ❌ {plan}
                                </div>
                              );
                          }
                        })()}
                        
                        {/* Type Badge */}
                        {(() => {
                          const typeInfo = getTypeConfig(girlData.type);
                          console.log(`👧 ${girlData.nickname}: type = "${girlData.type}" → ${typeInfo.icon} ${typeInfo.label}`);
                          
                          return (
                            <div className="bg-white/20 backdrop-blur-sm text-white px-1 py-0.5 rounded-full text-xs font-medium border border-white/30 sm:px-2 sm:py-1">
                              {typeInfo.icon} {typeInfo.label}
                            </div>
                          );
                        })()}
                      </div>
                        
                      {/* Manager Info */}
                      {girlData.momoName && (
                        <div className="flex items-center space-x-2">
                          <span className="text-white text-xs">👨‍💼 โม:</span>
                          <span className="text-white font-medium text-xs">
                            {girlData.momoName}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-1 sm:space-y-2">
                      <button 
                        onClick={() => window.location.href = `/girl/${girl.id}`}
                        className="w-full bg-green-500/80 backdrop-blur-sm text-white py-1.5 px-2 text-xs rounded-lg hover:bg-green-500 transition-all duration-200 border border-green-300/50 font-medium sm:py-2 sm:px-4 sm:text-sm"
                      >
                        จองคิวเลย
                      </button>
                      
                      {/* ปุ่มให้ของขวัญ - แสดงสำหรับทุกคนยกเว้นตัวเอง */}
                      {user && user.uid !== girl.id && (
                        <GiftButton
                          girlId={girl.id}
                          girlName={girlData.nickname}
                          className="w-full text-xs sm:text-sm py-1.5 px-2 sm:py-2 sm:px-4"
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 sm:mt-10 text-center">
          <p className="text-gray-600 text-sm sm:text-base">คลิกที่ปุ่มจองคิวเพื่อเข้าสู่หน้า profile และจองคิว</p>
          
          {/* ข้อความแจ้งเตือนทางกฎหมาย */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg max-w-2xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-blue-500 text-sm">📋</span>
                <div className="text-xs text-blue-700">
                  <span className="font-semibold">ข้อกำหนดการใช้งาน</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLegalDisclaimer(!showLegalDisclaimer)}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
              >
                {showLegalDisclaimer ? 'ซ่อน' : 'ดูรายละเอียด'}
              </button>
            </div>
            
            {showLegalDisclaimer && (
              <div className="mt-2 text-xs text-blue-700 text-center">
                การค้าประเวณีเป็นสิ่งผิดกฎหมายในประเทศไทย บริการนี้มีไว้เพื่อการพบปะสังสรรค์และกิจกรรมที่ถูกต้องตามกฎหมายเท่านั้น
              </div>
            )}
          </div>
        </div>

        {/* Debug: แสดงข้อมูล user */}
        {console.log('🏠 MapHome - user:', user, 'user exists:', !!user)}
        
        {/* แสดง loading หรือ error */}
        {loading && <div className="text-center text-blue-600 mb-4">กำลังโหลดข้อมูล...</div>}
        {error && (
          <div className="text-center text-red-600 mb-4 bg-red-100 p-3 rounded">
            <p>ไม่สามารถเชื่อมต่อฐานข้อมูลได้</p>
            <p className="text-sm">กรุณาติดต่อแอดมิน</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapHome;